/**
 * appointments-api — mock↔live dispatcher for the B1.4 WeeklyCalendar +
 * B1.5 AppointmentForm data layer.
 *
 * Delegates each call to either the `appointments-api-mock.ts`
 * (localStorage fixture) or `appointments-api-http.ts`
 * (`/api/biz/appointments/*`) implementation based on
 * `APP_ENV.bizApiMode`:
 *
 *   - `mock`  → useful in dev without a backend (default).
 *   - `live`  → hits the pet-servis Business surface (P1b BE).
 *
 * The signature contract is **frozen** — both impls expose identical
 * `Promise<...>` shapes so `useAppointments.ts` and the React Query
 * cache keys do not need to change between modes.
 *
 * Switching modes:
 *   Set `VITE_BIZ_API_MODE=live` in `web-business/.env.local` before
 *   running `npm run dev`.
 *
 * @see appointments-api-mock.ts
 * @see appointments-api-http.ts
 * @see backend/src/api/business/biz-booking-routes.ts
 * @see PETOTA_BUSINESS_UX_BLUEPRINT §B1.4
 */
import { APP_ENV } from '@/shared/config/env';

import * as httpApi from './appointments-api-http';
import * as mockApi from './appointments-api-mock';
import type {
  AppointmentRecord,
  CancelAppointmentPayload,
  CreateCashierOrderFromAppointmentPayload,
  CreateCashierOrderFromAppointmentResult,
  CreateAppointmentPayload,
  CustomerRecord,
  EditAppointmentPayload,
  PetRecord,
  ServiceRecord,
  StaffRecord,
  UpdateAppointmentPayload,
  WeekRangeQuery,
} from './types';

const useLive = APP_ENV.bizApiMode === 'live';

export async function listStaff(): Promise<StaffRecord[]> {
  return useLive ? httpApi.listStaff() : mockApi.listStaff();
}

export async function listAppointments(
  query: WeekRangeQuery,
): Promise<AppointmentRecord[]> {
  return useLive ? httpApi.listAppointments(query) : mockApi.listAppointments(query);
}

export async function updateAppointment(
  payload: UpdateAppointmentPayload,
): Promise<AppointmentRecord> {
  return useLive
    ? httpApi.updateAppointment(payload)
    : mockApi.updateAppointment(payload);
}

export async function cancelAppointment(
  payload: CancelAppointmentPayload,
): Promise<AppointmentRecord> {
  return useLive
    ? httpApi.cancelAppointment(payload)
    : mockApi.cancelAppointment(payload);
}

export async function createCashierOrderFromAppointment(
  payload: CreateCashierOrderFromAppointmentPayload,
): Promise<CreateCashierOrderFromAppointmentResult> {
  return useLive
    ? httpApi.createCashierOrderFromAppointment(payload)
    : mockApi.createCashierOrderFromAppointment(payload);
}

export async function listCustomers(): Promise<CustomerRecord[]> {
  return useLive ? httpApi.listCustomers() : mockApi.listCustomers();
}

export async function listPets(customerId?: string): Promise<PetRecord[]> {
  return useLive ? httpApi.listPets(customerId) : mockApi.listPets(customerId);
}

export async function listServices(): Promise<ServiceRecord[]> {
  return useLive ? httpApi.listServices() : mockApi.listServices();
}

export async function createAppointment(
  payload: CreateAppointmentPayload,
): Promise<AppointmentRecord> {
  return useLive
    ? httpApi.createAppointment(payload)
    : mockApi.createAppointment(payload);
}

export async function editAppointment(
  payload: EditAppointmentPayload,
): Promise<AppointmentRecord> {
  return useLive
    ? httpApi.editAppointment(payload)
    : mockApi.editAppointment(payload);
}

/**
 * Reset the localStorage fixture used by the mock impl. No-op in live
 * mode (delegates to the HTTP impl's no-op so callers don't need to
 * branch on `APP_ENV.bizApiMode`).
 */
export function resetAppointmentMock(): void {
  if (useLive) {
    httpApi.resetAppointmentMock();
    return;
  }
  mockApi.resetAppointmentMock();
}
